import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { site } from "@/data/site";
import { images } from "@/data/images";
import { Testimonials } from "@/components/home/Testimonials";
import { NextChapter } from "@/components/NextChapter";

// Programs highlighted in the Impact section.
const impact = [
  { role: "Head of Basketball Operations & Varsity Head Coach", org: "University Hill Secondary", hash: "university-hill" },
  { role: "Head Coach", org: "Empower Basketball", hash: "empower" },
  { role: "Founder & Lead Trainer", org: "Hooper's Delight Training", hash: "hoopers-delight" },
  { role: "Founder & Head Coach", org: "West Coast Falcons Basketball Club", hash: "falcons" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* ───────────── 1 · HERO ───────────── */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={images.heroCoach}
            alt="Coach Elete Gbolonyo courtside, watching his team practice"
            className="w-full h-full object-cover object-center scale-125 translate-x-[8%] -translate-y-[6%] brightness-[1.24] contrast-[0.98]"
          />
          {/* Left→right: near-black behind the text, easing out through the middle,
              staying clear on the right so Coach Elete remains visible. */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/98 from-0% via-black/70 via-45% to-transparent to-88%" />
          {/* Subtle extra darkening on the LEFT only — behind the name + mission —
              fading out before the centre so the coach and warm gym light are untouched. */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/35 from-0% via-black/18 via-28% to-transparent to-48%" />
          {/* Bottom anchor keeps the buttons readable */}
          <div className="absolute inset-0 bg-gradient-to-t from-ink-base via-ink-base/25 to-transparent" />
          {/* Mobile: text spans a narrower screen, so add a touch more overall depth */}
          <div className="absolute inset-0 bg-black/30 md:hidden" />
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 w-full pt-28 pb-20 relative z-10">
          <div className="max-w-3xl">
            <Reveal>
              <p className="label-eyebrow text-gold mb-6">
                Vancouver, BC, Canada&nbsp;&nbsp;·&nbsp;&nbsp;by way of Accra, Ghana
              </p>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="font-heading font-bold tracking-tighter leading-[0.92] text-5xl md:text-7xl lg:text-8xl text-white">
                {site.honorific}
                <br />
                {site.fullName}
              </h1>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-8 text-xl md:text-2xl text-white/90 font-heading font-light max-w-2xl leading-snug">
                Helping Student-Athletes Pursue Excellence On and Off the Court Through{" "}
                <span className="text-gold">Development Tailored to Each Individual.</span>
              </p>
            </Reveal>
            <Reveal delay={0.32}>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link
                  to="/philosophy"
                  data-testid="hero-cta-my-philosophy"
                  className="group inline-flex items-center gap-2 px-7 py-3.5 text-sm font-medium bg-burnt hover:bg-burnt-hover text-white transition-colors"
                >
                  My Philosophy
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/resume"
                  data-testid="hero-cta-my-impact"
                  className="group inline-flex items-center gap-2 px-7 py-3.5 text-sm font-medium border border-white/20 text-white hover:border-white hover:bg-white/5 transition-colors"
                >
                  My Impact
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-zinc-500">
          <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
          <ArrowDown size={16} className="animate-bounce text-burnt" />
        </div>
      </section>

      {/* ───────────── 2 · WHO I AM ───────────── */}
      <section className="py-24 md:py-36" data-testid="home-who">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <Reveal>
            <p className="label-eyebrow mb-8">Who I Am</p>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="font-heading text-2xl md:text-3xl lg:text-4xl font-light leading-[1.3] tracking-tight text-zinc-300 text-balance">
              <span className="text-white">My name is Elete Gbolonyo</span>, though
              some people know me as Lucius. I was born and raised in Ghana and moved
              to Canada at the age of 10. Since then, Canada has become a second home
              to me. My <span className="text-gold">faith is an important part of my
              life</span> and shapes the way I lead, serve, and coach. The experiences,
              lessons, and relationships I've gained through basketball continue to
              shape who I am today.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ───────────── 3 · WHAT I'M BUILDING ───────────── */}
      <section
        className="relative py-24 md:py-36 bg-ink-surface border-y border-white/10 overflow-hidden"
        data-testid="home-building"
      >
        <div className="grain absolute inset-0 opacity-60" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <Reveal>
              <div className="relative overflow-hidden border border-white/10 aspect-[4/5] max-w-md">
                <img
                  src={images.coachTeaching}
                  alt="Coach Elete coaching a player one-on-one on the court"
                  loading="lazy"
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-base/40 via-transparent to-transparent" />
              </div>
            </Reveal>
            <div>
              <Reveal>
                <p className="label-eyebrow text-gold mb-8">What I'm Building</p>
              </Reveal>
              <Reveal delay={0.06}>
                <p className="font-heading text-2xl md:text-3xl lg:text-4xl font-light leading-[1.25] tracking-tight text-white text-balance">
                  My goal isn't simply to win games. It's to build environments where
                  student-athletes can grow as{" "}
                  <span className="text-gold">players, teammates, and leaders.</span>
                </p>
              </Reveal>
              <Reveal delay={0.16}>
                <blockquote className="mt-10 pl-6 md:pl-8 border-l-2 border-burnt max-w-xl">
                  <p className="font-heading text-xl md:text-2xl font-light text-white italic leading-snug">
                    "My job: build an environment where people can do their best work."
                  </p>
                  <footer className="mt-4 text-xs uppercase tracking-[0.2em] text-gold not-italic">
                    NBA General Manager
                  </footer>
                </blockquote>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── 4 · IMPACT / EXPERIENCE ───────────── */}
      <section className="py-24 md:py-36" data-testid="home-impact">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
            <Reveal>
              <p className="label-eyebrow mb-4">Impact</p>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white max-w-xl text-balance">
                The programs and communities I'm proud to be a part of.
              </h2>
            </Reveal>
            <Reveal>
              <Link
                to="/resume"
                data-testid="impact-experience-link"
                className="group inline-flex items-center gap-2 text-white font-medium whitespace-nowrap"
              >
                See the full experience
                <ArrowRight size={16} className="text-burnt transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {impact.map((p, i) => (
              <Reveal key={p.org} delay={i * 0.08} className="h-full">
                <Link
                  to={`/resume#${p.hash}`}
                  data-testid={`impact-card-${i}`}
                  className="group bg-ink-base p-8 md:p-10 h-full flex flex-col hover:bg-ink-surface transition-colors"
                >
                  <span className="font-mono text-sm text-burnt">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="mt-6 text-[11px] uppercase tracking-[0.2em] text-gold">
                    {p.role}
                  </p>
                  <h3 className="mt-2 font-heading text-xl md:text-2xl font-semibold text-white leading-snug group-hover:text-gold transition-colors">
                    {p.org}
                  </h3>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── 4.5 · IN THEIR WORDS ───────────── */}
      <Testimonials />

      {/* Next chapter → Philosophy (single closing action) */}
      <NextChapter to="/philosophy" label="Explore My Philosophy" />
    </div>
  );
}
